import React, { useMemo, useState } from 'react';
import styles from './SingleForm.module.scss';
import Header from 'component/layout/header/Header';
import { useFetch } from 'hooks/useFetch';
import { categoriesEndpoints, formsEndpoints } from 'hooks/endpoints';
import { useNavigate, useParams } from 'react-router-dom';
import FormInfoCard from 'component/organism/form-responses/form-info-card/FormInfoCard';
import TableComponent from 'component/molecules/Table/TableComponent';
import { headerData } from './factory/headerData';
import Button from 'component/atom/forms/button/Button';
import Loader from 'component/atom/loader/Loader';
import SearchInput from 'component/atom/forms/input/searchInput/SearchInput';
import Select from 'component/atom/forms/select/select/Select';

const SingleForm = () => {
  const { formId, botId } = useParams();
  const navigate = useNavigate();

  const { data: allCategories } = useFetch(
    ['allCategories', botId],
    categoriesEndpoints.getParentCategories,
    { enabled: true },
    { BotId: botId }
  );

  const { data: formResponses, isFetching } = useFetch(
    ['forms-responses', formId],
    formsEndpoints.getFormResponseByFormId,
    { enabled: !!formId },
    { FormId: formId }
  );

  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const categoryOptions = useMemo(() => {
    const items = allCategories?.data?.items ?? [];
    const mapped = items.map((c) => ({
      value: c.categoriesId,
      label: c.categoryText,
    }));
    return [{ value: '', label: 'All Categories' }, ...mapped];
  }, [allCategories]);

  const categoryFiltered = useMemo(() => {
    const items = formResponses?.data?.result?.items ?? [];
    if (!selectedCategoryId) return items;
    return items.filter((i) => i.categoriesId === selectedCategoryId);
  }, [formResponses, selectedCategoryId]);

  const visibleItems = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return categoryFiltered;
    return categoryFiltered.filter((i) =>
      String(i.categoryText ?? '')
        .toLowerCase()
        .includes(q)
    );
  }, [categoryFiltered, searchValue]);

  const renderDetailsButton = (item) => {
    return (
      <Button
        label='View Details'
        onClick={(e) => {
          e.stopPropagation();
          navigate(
            `/bot/form-leads/${formId}/${item?.item?.categoriesId}/${botId}`
          );
        }}
        variant='primary-rev'
        className={styles['view-details-btn']}
      />
    );
  };

  const handleSearchChange = (_name, value) => setSearchValue(value ?? '');

  return (
    <div>
      <Header
        title='Form Response'
        isBack
        isSub
        titleClassName={styles['title']}
      />
      <div className={styles['form-responses-container']}>
        <div className={styles['form-information-container']}>
          <FormInfoCard
            title='Total Leads'
            total={formResponses?.data?.result?.totalLeads[0]?.totalCount}
            icon='total-leads'
          />
          <FormInfoCard
            title='High Intent Leads'
            total={
              formResponses?.data?.result?.activeConversations[0]?.totalCount
            }
            subTitle='Intent Score 4-5'
            icon='high-intent-leads'
          />
          <FormInfoCard
            title='Files Submitted'
            total={formResponses?.data?.result?.filesSubmitted[0]?.totalCount}
            subTitle='Documents Received'
            icon='files-submitted'
          />
        </div>

        {isFetching && <Loader isComponent />}

        {!isFetching && (
          <div className={styles['table-wrapper']}>
            <div className={styles['table-header']}>
              <span className={styles['title']}>Degree Programs</span>
              <div className={styles['filter-items']}>
                <SearchInput
                  onChange={handleSearchChange}
                  value={searchValue}
                  placeholder='Search'
                  classname={styles['search']}
                />
                <Select
                  options={categoryOptions}
                  value={selectedCategoryId}
                  placeholder='All Categories'
                  onChange={(e) => setSelectedCategoryId(e.target.value || '')}
                  classname={styles['select']}
                />
              </div>
            </div>
            <TableComponent
              rowKey='categoriesId'
              headerData={headerData}
              externalData={visibleItems}
              renderAdditionalComponent={renderDetailsButton}
              hasAction={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SingleForm;
